import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, ShieldAlert, CheckCircle2, X, ArrowRight } from "lucide-react";

// Single-blocker setup-health widget. Drops the long blocker
// list a manager doesn't have time for and surfaces just the next action
// for the page they're on. Dismissible per session.

export default function SetupHealthCard({ page }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(`setup-health-dismissed:${page}`) === "1"; }
    catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/automation/page-health?page=${page}`);
      setData(data);
    } catch { setData(null); }
  }, [page]);

  useEffect(() => { refresh(); }, [refresh]);

  if (dismissed || !data || !data.has_blocker) return null;
  const b = data.blocker || {};
  const fix = data.fix_hint;

  async function generate() {
    if (!fix?.only_kinds) return;
    setBusy(true);
    try {
      const status = await api.get("/setup/wizard-status").catch(() => ({ data: {} }));
      const templateId = status.data?.recommended_template_id || "office_small";
      const { data: result } = await api.post("/setup/wizard-complete", {
        template_id: templateId,
        industry:  status.data?.industry,
        size_band: status.data?.size_band,
        only_kinds: fix.only_kinds,
        dry_run: false,
      });
      const created = Object.values(result.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Created ${created} item${created === 1 ? "" : "s"}. Existing items were kept untouched.`);
      await refresh();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not generate");
    } finally { setBusy(false); }
  }

  function dismiss() {
    try { sessionStorage.setItem(`setup-health-dismissed:${page}`, "1"); } catch {}
    setDismissed(true);
  }

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 mb-4 flex items-start gap-3"
         data-testid={`setup-health-${page}`}>
      <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-medium text-sm text-amber-900">{b.label || "Setup needs attention"}</h3>
          {data.level_blocked && (
            <span className="text-[10px] uppercase tracking-widest text-amber-800 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200">
              Blocks {data.level_blocked}
            </span>
          )}
        </div>
        {b.why_it_matters && (
          <p className="text-xs text-amber-900 mt-1" data-testid={`setup-health-${page}-why`}>
            {b.why_it_matters}
          </p>
        )}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {fix?.only_kinds && (
            <Button size="sm" onClick={generate} disabled={busy}
                    className="h-7 text-xs bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                    data-testid={`setup-health-${page}-fix`}>
              {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
              {fix.label}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate("/app/setup")}
                  className="h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
                  data-testid={`setup-health-${page}-hub`}>
            Open Setup overview <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>
      <button type="button" onClick={dismiss}
              className="text-amber-700 hover:text-amber-900"
              aria-label="Dismiss"
              data-testid={`setup-health-${page}-dismiss`}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
